import { useId, useRef, useState } from "react";
import styles from "./TabsComp.module.css";

export default function TabsComp({
  items = [],
  initialActiveIndex = 0,
  showNumbers = false,
  showIcons = false,
  showNavigation = false,
  ariaLabel = "Tabs",
}) {
  const safeInitialIndex =
    items.length > 0
      ? Math.min(Math.max(initialActiveIndex, 0), items.length - 1)
      : 0;
  const [activeIndex, setActiveIndex] = useState(safeInitialIndex);
  const instanceId = useId();
  const railRef = useRef(null);

  if (!items.length) {
    return null;
  }

  const activeItem = items[activeIndex];

  function scrollTabs(direction) {
    if (!railRef.current) {
      return;
    }

    const amount = Math.max(railRef.current.clientWidth * 0.75, 220);

    railRef.current.scrollBy({
      left: direction === "next" ? amount : -amount,
      behavior: "smooth",
    });
  }

  return (
    <div className={styles.tabs}>
      <div className={styles.header}>
        <div
          ref={railRef}
          className={styles.rail}
          role="tablist"
          aria-label={ariaLabel}
        >
          {items.map((item, index) => {
            const tabId = `${instanceId}-tab-${index}`;
            const panelId = `${instanceId}-panel-${index}`;
            const isActive = activeIndex === index;
            const itemNumber = String(index + 1).padStart(2, "0");

            return (
              <button
                key={item.title}
                type="button"
                id={tabId}
                role="tab"
                aria-selected={isActive}
                aria-controls={panelId}
                tabIndex={isActive ? 0 : -1}
                className={`${styles.tabButton} ${isActive ? styles.tabButtonActive : ""}`}
                onClick={() => setActiveIndex(index)}
              >
                {(showNumbers || (showIcons && item.icon)) && (
                  <span className={styles.leadingWrap}>
                    {showNumbers && (
                      <span className={styles.tabIndex}>{itemNumber}</span>
                    )}
                    {showIcons && item.icon && (
                      <span className={styles.tabIcon} aria-hidden="true">
                        {item.icon}
                      </span>
                    )}
                  </span>
                )}
                <span className={styles.tabTitle}>{item.title}</span>
              </button>
            );
          })}
        </div>

        {showNavigation && items.length > 1 && (
          <div className={styles.navActions}>
            <button
              type="button"
              className={styles.navButton}
              onClick={() => scrollTabs("prev")}
              aria-label="Scroll tabs left"
            >
              <span aria-hidden="true">&lt;</span>
            </button>
            <button
              type="button"
              className={styles.navButton}
              onClick={() => scrollTabs("next")}
              aria-label="Scroll tabs right"
            >
              <span aria-hidden="true">&gt;</span>
            </button>
          </div>
        )}
      </div>

      <div
        id={`${instanceId}-panel-${activeIndex}`}
        role="tabpanel"
        aria-labelledby={`${instanceId}-tab-${activeIndex}`}
        className={styles.panel}
      >
        <span className={styles.panelEyebrow}>Process Step</span>
        <h3 className={styles.panelTitle}>{activeItem.title}</h3>
        <p className={styles.panelText}>{activeItem.text}</p>
      </div>
    </div>
  );
}
